import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm, Controller, useFieldArray, FormProvider } from "react-hook-form";
import CloseIconBtn from "src/components/shared/CloseIconBtn";
import LeftArrow from "src/components/shared/icon/LeftArrow";
import UpFileBtn from "src/components/shared/UpFileBtn";
import useQueryParams from "src/hooks/useQueryParams";
import useProductSchema from "../utils/useProductSchema";
import useProductCreate from "src/hooks/product/useProductCreate";
import { enqueueSnackbar } from "notistack";
import useProductDetail from "src/hooks/product/useProductDetail";
import useProductUpdate from "src/hooks/product/useProductUpdate";
import LoadingComp from "src/components/shared/LoadingComp";
import useProductModelRemove from "src/hooks/product/useProductModelRemove";
import ProductDetailView from "./ProductDetailView";
import { ACTION_TYPE, UNIT_LIST, UNIT_LIST_CONVERT } from "src/constant";
import useProdGroupDetail from "src/hooks/product-groups/useProdGroupDetail";
import useProdGroups from "src/hooks/product-groups/useProdGroups";
import { convertCurrencyToNumber } from "src/helper/convertToNumber";
import convertCurrency from "src/helper/convertCurrency";
import { Add } from "@mui/icons-material";
import FormControlInput from "src/components/shared/FormControlInput";
import FormControlNumericInput from "src/components/shared/FormControlNumericInput";
import FormControlSelect from "src/components/shared/FormControlSelect";
import AutoComplete from "src/components/shared/AutoComplete";
import useDebounce from "src/hooks/useDebounce";
import useInfiniteAutocomplete from "src/hooks/useInfiniteAutocomplete";
import FieldLabel from "src/components/shared/FieldLabel";
import { initialProd } from "../utils/productValidation";

export default function ProductDetail() {
  const { data: prodGroupList } = useProdGroups({
    sortField: "order",
    sortOrder: "asc",
  });
  const [option, setOption] = useQueryParams();
  const [prodGroup, setProdGroup] = useState(prodGroupList?.data?.[0]?._id);
  const [searchBrand, setSearchBrand] = useState("");
  const [searchManufacturer, setSearchManufacturer] = useState("");
  const [brandAutocompleteKey, setBrandAutocompleteKey] = useState(0);
  const [manufacturerAutocompleteKey, setManufacturerAutocompleteKey] = useState(0);
  const debouncedSearchBrand = useDebounce(searchBrand, 500);
  const debouncedSearchManufacturer = useDebounce(searchManufacturer, 500);
  const { data: prodGroupDetail, isLoading: prodGroupDetailLoading } = useProdGroupDetail(prodGroup ? prodGroup : prodGroupList?.data?.[0]?._id);
  const { validationSchema } = useProductSchema(prodGroupDetail?.data?.code);
  const isRetailProductGroup = !prodGroupDetail?.data?.code;

  const {
    items: brandItems,
    isLoading: brandLoading,
    loadingMore: brandLoadingMore,
    hasMore: brandHasMore,
    onLoadMore: onLoadMoreBrand,
  } = useInfiniteAutocomplete(
    "/api/v1/brands",
    {
      limit: 10,
      searchField: "name",
      searchValue: debouncedSearchBrand || undefined,
      sortField: "createdAt",
      sortOrder: "desc",
    },
    isRetailProductGroup,
    debouncedSearchBrand,
  );

  const {
    items: manufacturerItems,
    isLoading: manufacturerLoading,
    loadingMore: manufacturerLoadingMore,
    hasMore: manufacturerHasMore,
    onLoadMore: onLoadMoreManufacturer,
  } = useInfiniteAutocomplete(
    "/api/v1/manufacturers",
    { limit: 10, searchField: "name", searchValue: debouncedSearchManufacturer || undefined },
    !isRetailProductGroup,
    debouncedSearchManufacturer,
  );

  const { trigger: createProduct } = useProductCreate();
  const { data: prodDetail, isLoading } = useProductDetail(option?.productId);
  const { trigger: updateProdInfo } = useProductUpdate(option?.productId);
  const { trigger: removeProdModel } = useProductModelRemove(option?.productId);
  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues: initialProd,
    shouldUnregister: false,
  });
  const brandWatch = form.watch("brand");
  const manufacturerWatch = form.watch("manufacturer");
  const unit = form.watch("unit");

  const brandOptions = useMemo(() => {
    const mapped = brandItems.map((v) => ({
      label: v?.name,
      value: v?._id,
      code: v?.code,
    }));
    const selectedId = brandWatch;
    if (selectedId && !mapped.some((item) => item.value === selectedId)) {
      const selectedBrand = prodDetail?.data?.brand;
      if (selectedBrand?._id === selectedId) {
        mapped.unshift({
          label: selectedBrand?.name,
          value: selectedBrand?._id,
          code: selectedBrand?.code,
        });
      }
    }
    return mapped;
  }, [brandItems, brandWatch, prodDetail?.data?.brand]);

  const manufacturerOptions = useMemo(() => {
    const mapped = manufacturerItems.map((v) => ({
      label: v?.name ?? "-",
      value: v?._id,
    }));
    const selectedId = manufacturerWatch;
    if (selectedId && !mapped.some((item) => item.value === selectedId)) {
      const selectedManufacturer = prodDetail?.data?.manufacturer;
      const manufacturerId = typeof selectedManufacturer === "object" ? selectedManufacturer?._id : selectedManufacturer;
      if (manufacturerId === selectedId && typeof selectedManufacturer === "object") {
        mapped.unshift({
          label: selectedManufacturer?.name ?? "-",
          value: manufacturerId,
        });
      }
    }
    return mapped;
  }, [manufacturerItems, manufacturerWatch, prodDetail?.data?.manufacturer]);

  const brandMap = useMemo(() => new Map(brandOptions.map((brand) => [brand.value, brand])), [brandOptions]);

  const resetAutocompleteState = () => {
    setSearchBrand("");
    setSearchManufacturer("");
    setBrandAutocompleteKey((prev) => prev + 1);
    setManufacturerAutocompleteKey((prev) => prev + 1);
  };

  const handleInputChangeBrand = (_, value, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchBrand("");
      return;
    }
    setSearchBrand(value);
  };

  const handleInputChangeManufacturer = (_, value, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchManufacturer("");
      return;
    }
    setSearchManufacturer(value);
  };

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "productModels",
  });

  useEffect(() => {
    if (prodGroupList?.data) {
      if (!prodDetail?.data?.productGroup) {
        setProdGroup(prodGroupList?.data[0]?._id);
      } else {
        setProdGroup(prodDetail?.data?.productGroup?._id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodGroupList?.data, prodDetail?.data?.productGroup?._id]);

  useEffect(() => {
    if (!isLoading && prodDetail?.data && option?.productId) {
      const prodData = {
        brand: prodDetail.data.brand?._id,
        notificationNo: prodDetail.data.notificationNo,
        nameVi: prodDetail.data.nameVi,
        name: prodDetail.data.name,
        image: prodDetail.data.image,
        code: prodDetail?.data?.code?.replace(prodGroupDetail?.data?.code, ""),
        shortName: prodDetail.data.shortName,
        productModels: prodDetail.data.productModels?.map((v) => ({ ...v, code: v?.code?.replace(`${prodDetail?.data?.brand?.code}-`, "") })) ?? [],
        manufacturer: typeof prodDetail.data.manufacturer === "object" ? prodDetail.data.manufacturer?._id : prodDetail.data.manufacturer,
        vat: prodDetail.data.vat,
        unit: prodDetail?.data?.unit,
        description: prodDetail?.data?.description,
        shelfLife: prodDetail?.data?.shelfLife,
      };
      form.reset(prodData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, prodDetail?.data, option?.productId, prodGroup, prodGroupDetail?.data?.code]);

  useEffect(() => {
    if (!option?.productId && prodGroupDetail?.data?.vat !== undefined && prodGroupDetail?.data?.vat !== null) {
      form.setValue("vat", String(prodGroupDetail?.data?.vat), { shouldDirty: false });
    }
  }, [form, option?.productId, prodGroupDetail?.data?.vat]);

  const handleCreateProd = async (data) => {
    let body;

    const hasProductModels = data?.productModels?.some((v) =>
      [v?.name, v?.barcode, v?.code, v?.image, v?.price, v?.vat, v?.shippingInfo?.weight, v?.shippingInfo?.length, v?.shippingInfo?.width, v?.shippingInfo?.height].some(
        (value) => value !== null && value !== undefined && value !== "",
      ),
    );

    if (!prodGroupDetail?.data?.code) {
      body = {
        ...data,
        image: data?.image || null,
        vat: Number(data?.vat) > 0 ? Number(data?.vat) : null,
        manufacturer: null,
        unit: data?.unit,
        productGroup: prodGroupDetail?.data?._id,

        productModels: hasProductModels
          ? data.productModels.map((v) => ({
              ...v,
              image: v?.image || null,
              shippingInfo: v?.shippingInfo
                ? {
                    ...v.shippingInfo,
                    weight: v.shippingInfo.weight === "" ? null : v.shippingInfo.weight,
                    length: v.shippingInfo.length === "" ? null : v.shippingInfo.length,
                    width: v.shippingInfo.width === "" ? null : v.shippingInfo.width,
                    height: v.shippingInfo.height === "" ? null : v.shippingInfo.height,
                  }
                : null,
            }))
          : [],
      };
    } else {
      body = {
        ...data,
        image: data?.image || null,
        vat: Number(data?.vat) > 0 ? Number(data?.vat) : null,
        productGroup: prodGroupDetail?.data?._id,
        brand: undefined,
        code: data?.code,

        productModels: hasProductModels
          ? data.productModels.map((v) => ({
              ...v,
              vat: Number(v?.vat),
              code: null,
              price: v?.price ? v.price : null,
              image: v?.image || null,
              shippingInfo: v?.shippingInfo
                ? {
                    ...v.shippingInfo,
                    weight: v.shippingInfo.weight === "" ? null : v.shippingInfo.weight,
                    length: v.shippingInfo.length === "" ? null : v.shippingInfo.length,
                    width: v.shippingInfo.width === "" ? null : v.shippingInfo.width,
                    height: v.shippingInfo.height === "" ? null : v.shippingInfo.height,
                  }
                : null,
            }))
          : [],
      };
    }

    const res = await createProduct({ body });

    if (res?.success) {
      resetAutocompleteState();
      form.reset(initialProd);
      setOption({ ...option, status: undefined });

      return enqueueSnackbar("Tạo sản phẩm thành công", {
        variant: "success",
      });
    }

    return enqueueSnackbar(res?.error?.message, {
      variant: "error",
    });
  };

  const handleAddMoreClassification = () => {
    append(initialProd.productModels[0]);
  };

  const handleRedirectProduct = () => {
    resetAutocompleteState();
    form.reset(initialProd);
    setOption({ ...option, status: undefined, productId: undefined });
  };

  const handleUpdateProdInfo = async () => {
    const data = form.getValues();
    const bodyData = {
      ...prodDetail?.data,
      ...data,
      image: typeof data?.image === "string" ? data?.image : prodDetail?.data?.image?._id,
      productGroup: prodGroupDetail?.data?._id,
    };
    let body;
    const isError = bodyData.productModels.some((v) => {
      const req = /^[a-zA-Z0-9]{1,}.+/;
      return !req.test(v.code);
    });
    if (isError) {
      enqueueSnackbar("Mã phân loại chỉ bắt đầu bằng chữ cái hoặc số.", { variant: "error" });
      return;
    }

    if (prodGroupDetail?.data?.code) {
      body = {
        ...bodyData,
        brand: undefined,
        vat: Number(bodyData?.vat),
        productModels: [
          ...bodyData?.productModels.map((v) => ({
            ...v,
            image: v?.image?._id ?? v?.image,
            shippingInfo: null,
            price: typeof v?.price === "string" ? convertCurrencyToNumber(v?.price) : Number(v?.price),
          })),
        ],
      };
    } else {
      body = {
        ...bodyData,
        vat: Number(bodyData?.vat),
        productModels: [
          ...bodyData?.productModels?.map((v) => ({
            ...v,
            vat: Number(v?.vat),
            image: typeof v?.image === "object" ? v?.images?._id : v?.image,
            shippingInfo: {
              weight: Number(v?.shippingInfo?.weight),
              length: Number(v?.shippingInfo?.length),
              width: Number(v?.shippingInfo?.width),
              height: Number(v?.shippingInfo?.height),
            },
            price: typeof v?.price === "string" ? convertCurrencyToNumber(v?.price) : Number(v?.price),
          })),
        ],
      };
    }
    const res = await updateProdInfo({
      body: { ...body, productModels: body.productModels.map((v) => ({ ...v, code: `${brandMap.get(brandWatch)?.code}-${v?.code}` })) },
    });
    if (res?.success) {
      resetAutocompleteState();
      setOption({ ...option, status: undefined, productId: undefined });
      return enqueueSnackbar("Thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const handleRemoveProdModel = async (item) => {
    if (item?._id) {
      const res = await removeProdModel({
        pathIncluded: { prodModelId: item?._id },
      });
      if (res?.success) {
        return enqueueSnackbar("Thành công", { variant: "success" });
      } else {
        return enqueueSnackbar(res?.error?.message ?? "Lỗi khi xóa mô hình sản phẩm", { variant: "error" });
      }
    }
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(handleCreateProd)}
        style={{ width: "100%" }}
      >
        {/* TITLE */}
        <Grid
          xs={12}
          md={12}
          item
          sx={{
            borderBottom: "1px solid var(--light-grey)",
            position: "sticky",
            top: 0,
            zIndex: 2,
            background: "var(--white)",
            width: "100%",
          }}
        >
          <Box p="12px">
            <Box
              display="flex"
              alignItems="center"
              gap="6px"
            >
              <IconButton
                onClick={handleRedirectProduct}
                sx={{
                  width: "28px",
                  height: "28px",
                  background: "var(--black) !important",
                }}
              >
                <LeftArrow color="var(--white)" />
              </IconButton>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
              >
                Danh sách sản phẩm
              </Typography>
            </Box>
            {option?.status !== "view" ? (
              <Typography
                fontWeight="bold"
                fontSize="16px"
                variant="subtitle2"
                m="6px 0px 2px"
              >
                {!!option?.productId ? "Sửa" : "Thêm"} sản phẩm
              </Typography>
            ) : (
              <Typography>{prodDetail?.data?.name ?? "-"}</Typography>
            )}
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          mt={2}
          ml={2}
        >
          {Array.isArray(prodGroupList?.data) && prodGroup ? (
            <FormControl>
              <FormLabel id="demo-row-radio-buttons-group-label">
                <Typography fontWeight="bold">Nhóm sản phẩm</Typography>
              </FormLabel>
              <RadioGroup
                row
                onChange={(e) => {
                  const newProdGroup = e.target.value;

                  setProdGroup(newProdGroup);

                  if (option?.status === ACTION_TYPE.Create) {
                    form.reset(initialProd);
                    resetAutocompleteState();
                  }
                }}
                value={prodGroup}
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
              >
                {prodGroupList?.data?.map((v) => {
                  return (
                    <FormControlLabel
                      key={v?._id}
                      value={v?._id}
                      control={<Radio />}
                      label={v?.name}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          ) : !prodGroupList?.data?.length ? (
            <Typography>Không có nhóm sản phẩm</Typography>
          ) : (
            <Skeleton
              width="100%"
              height="40px"
            />
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ p: "12px 12px 12px" }}
        >
          <Typography
            fontWeight="bold"
            sx={{
              borderBottom: "1px solid var(--grey)",
              mb: "12px",
              p: 1,
              bgcolor: "var(--grey)",
            }}
          >
            Thông tin cơ bản của sản phẩm
          </Typography>
        </Grid>
        {option?.status !== "view" ? (
          <>
            {!isLoading ? (
              <Grid
                xs={12}
                item
                container
                spacing={2}
                sx={{ p: "12px" }}
              >
                {/* INFO */}
                <Grid
                  xs={12}
                  item
                  sx={{ display: "flex", gap: "12px" }}
                >
                  <Box>
                    <Controller
                      name={"image"}
                      control={form.control}
                      rules={{ required: "Image is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <UpFileBtn
                          value={field.value}
                          error={!!error}
                          onChange={(fileId) => {
                            field.onChange(fileId);
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    <FormControlInput
                      name="name"
                      label={!prodGroupDetail?.data?.code ? "Tên sản phẩm" : "Tên thương mại"}
                      required
                      disabled={option?.status === "view"}
                    />
                    <Box
                      display="flex"
                      gap="12px"
                    >
                      <Box width="100%">
                        {!prodGroupDetailLoading ? (
                          <FormControlInput
                            key={prodGroup}
                            name={!prodGroupDetail?.data?.code ? "nameVi" : "code"}
                            label={!prodGroupDetail?.data?.code ? "Tên tiếng Việt" : "Code"}
                            required={prodGroupDetail?.data?.code}
                            disabled={option?.status === "view"}
                            slotProps={{
                              input: {
                                startAdornment: prodGroupDetail?.data?.code && <InputAdornment>{prodGroupDetail?.data?.code} </InputAdornment>,
                              },
                            }}
                          />
                        ) : (
                          <Skeleton width="100%" />
                        )}
                      </Box>
                      <Box width="100%">
                        <FormControlInput
                          name="shortName"
                          label="Tên viết tắt"
                          disabled={option?.status === "view"}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12 / 2}
                >
                  <FormControlSelect
                    name="unit"
                    label="Đơn vị tính"
                    required
                    disabled={option?.status === "view"}
                    options={UNIT_LIST?.map((v) => ({ label: v?.label, value: v?.id }))}
                  />
                </Grid>
                <Grid
                  xs={12 / 2}
                  item
                >
                  <FormControlInput
                    name="vat"
                    label="VAT (%)"
                    required
                    disabled={option?.status === "view"}
                  />
                </Grid>
                {!prodGroupDetail?.data?.code && (
                  <Grid
                    xs={12}
                    item
                  >
                    <Controller
                      name="brand"
                      control={form.control}
                      render={({ field, fieldState: { error } }) => (
                        <AutoComplete
                          key={`brand-${brandAutocompleteKey}`}
                          fullWidth
                          disabled={option?.status === "view"}
                          options={brandOptions}
                          value={brandOptions.find((item) => item.value === field.value) || null}
                          onChange={(_, value) => field.onChange(value?.value ?? "")}
                          getOptionLabel={(option) => option?.label ?? ""}
                          isOptionEqualToValue={(option, value) => option?.value === value?.value}
                          loading={brandLoading}
                          hasMore={brandHasMore}
                          loadingMore={brandLoadingMore}
                          onLoadMore={onLoadMoreBrand}
                          filterOptions={(opts) => opts}
                          onInputChange={handleInputChangeBrand}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={
                                <FieldLabel
                                  label="Thương hiệu"
                                  required
                                />
                              }
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                )}
                {!prodGroupDetail?.data?.code && (
                  <>
                    <Grid
                      xs={12}
                      item
                    >
                      <FormControlInput
                        name="notificationNo"
                        label="Số phiếu công bố"
                        required
                        disabled={option?.status === "view"}
                      />
                    </Grid>
                  </>
                )}
                {prodGroupDetail?.data?.code && (
                  <Grid
                    xs={12}
                    item
                  >
                    <Controller
                      disabled={option?.status === "view"}
                      name="manufacturer"
                      control={form.control}
                      render={({ field, fieldState: { error } }) => (
                        <AutoComplete
                          key={`manufacturer-${manufacturerAutocompleteKey}`}
                          fullWidth
                          disabled={option?.status === "view"}
                          options={manufacturerOptions}
                          value={manufacturerOptions.find((item) => item.value === field.value) || null}
                          onChange={(_, value) => field.onChange(value?.value ?? "")}
                          getOptionLabel={(option) => option?.label ?? ""}
                          isOptionEqualToValue={(option, value) => option?.value === value?.value}
                          loading={manufacturerLoading}
                          hasMore={manufacturerHasMore}
                          loadingMore={manufacturerLoadingMore}
                          onLoadMore={onLoadMoreManufacturer}
                          filterOptions={(opts) => opts}
                          onInputChange={handleInputChangeManufacturer}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={
                                <FieldLabel
                                  label="Nhà sản xuất"
                                  required
                                />
                              }
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                )}

                {prodGroupDetail?.data?.code && (
                  <>
                    <Grid
                      item
                      xs={12}
                    >
                      <FormControlInput
                        name="shelfLife"
                        label="Shelf life"
                        required
                        disabled={option?.status === "view"}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                    >
                      <FormControlInput
                        name="description"
                        label="Mô tả kỹ thuật"
                        disabled={option?.status === "view"}
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </>
                )}

                {/* PRODUCT MODEL */}
                <Grid
                  item
                  xs={12}
                  mt={2}
                  sx={{ p: "12px 12px 12px" }}
                >
                  <Typography
                    fontWeight="bold"
                    sx={{
                      borderBottom: "1px solid var(--grey)",
                      p: 1,
                      bgcolor: "var(--grey)",
                    }}
                  >
                    Chi tiết phân loại sản phẩm
                  </Typography>
                </Grid>

                {fields.map((item, index) => {
                  const money = form.watch(`productModels.${index}.price`);
                  return (
                    <Grid
                      container
                      xs={12}
                      item
                      spacing={2}
                      key={item.id}
                    >
                      {index !== 0 && (
                        <Grid
                          item
                          xs={12}
                          align="right"
                        >
                          <CloseIconBtn
                            onClick={() => {
                              remove(index);
                              handleRemoveProdModel(item);
                            }}
                          />
                        </Grid>
                      )}

                      {/* Image Field */}
                      <Grid
                        xs={12}
                        item
                        sx={{ display: "flex", gap: "12px" }}
                      >
                        <Controller
                          name={`productModels.${index}.image`}
                          control={form.control}
                          rules={{ required: isRetailProductGroup ? "Image is required" : false }}
                          render={({ field, fieldState: { error } }) => (
                            <UpFileBtn
                              value={field?.value}
                              onChange={(file) => field.onChange(file)}
                              error={error}
                            />
                          )}
                        />
                        {/* PRODUCT MODEL DETAIL */}
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <FormControlInput
                            name={`productModels.${index}.name`}
                            label="Phân loại"
                            required={isRetailProductGroup}
                            disabled={option?.status === "view"}
                            sx={{ mb: "12px" }}
                          />
                          {prodGroupDetail?.data?.code && (
                            <Grid
                              item
                              xs={12}
                              container
                            >
                              <Box
                                display="flex"
                                width="100%"
                                gap={1}
                              >
                                <Box width="100%">
                                  <FormControlNumericInput
                                    name={`productModels.${index}.price`}
                                    label={`Đơn giá trước VAT (1 ${UNIT_LIST_CONVERT[unit] ?? "-"})`}
                                    required={isRetailProductGroup}
                                    disabled={option?.status === "view"}
                                    prefix="đ"
                                    thousandSeparator
                                  />
                                  {form.watch(`productModels.${index}.price`) ? (
                                    <Typography
                                      fontWeight="bold"
                                      fontStyle="italic"
                                      fontSize="10px"
                                    >
                                      {typeof form.watch(`productModels.${index}.price`) === "string"
                                        ? convertCurrency(convertCurrencyToNumber(form.watch(`productModels.${index}.price`)))
                                        : convertCurrency(form.watch(`productModels.${index}.price`))}{" "}
                                      đồng
                                    </Typography>
                                  ) : null}
                                </Box>
                                <FormControlInput
                                  name={`productModels.${index}.sku`}
                                  label="Mã SKU"
                                  required={isRetailProductGroup && !prodGroupDetail?.data?.code}
                                  disabled={option?.status === "view"}
                                />
                              </Box>
                            </Grid>
                          )}
                          <Box
                            display="flex"
                            gap={1}
                          >
                            {!prodGroupDetail?.data?.code && (
                              <>
                                <Grid
                                  item
                                  xs={prodGroupDetail?.data?.code ? 12 / 2 : 12 / 3}
                                >
                                  <FormControlInput
                                    name={`productModels.${index}.code`}
                                    label="Mã phân loại"
                                    required={isRetailProductGroup}
                                    disabled={option?.status === "view"}
                                    placeholder={!!brandMap.get(brandWatch)?.code ? "01" : "DRC-01"}
                                    slotProps={{
                                      input: {
                                        startAdornment: (
                                          <InputAdornment>
                                            <>{!!brandMap.get(brandWatch)?.code ? brandMap.get(brandWatch)?.code + "-" : ""}</>
                                          </InputAdornment>
                                        ),
                                      },
                                    }}
                                  />
                                </Grid>

                                <Grid
                                  item
                                  xs={prodGroupDetail?.data?.code ? 12 / 2 : 12 / 3}
                                >
                                  <FormControlInput
                                    name={`productModels.${index}.barcode`}
                                    label="Mã vạch"
                                    required={isRetailProductGroup}
                                    disabled={option?.status === "view"}
                                  />
                                </Grid>
                              </>
                            )}
                            {!prodGroupDetail?.data?.code && (
                              <Grid
                                item
                                xs={prodGroupDetail?.data?.code ? 12 / 2 : 12 / 3}
                              >
                                <FormControlInput
                                  name={`productModels.${index}.sku`}
                                  label="Mã SKU"
                                  required={isRetailProductGroup && !prodGroupDetail?.data?.code}
                                  disabled={option?.status === "view"}
                                />
                              </Grid>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid
                        item
                        md={12}
                        container
                        spacing={2}
                      >
                        {!prodGroupDetail?.data?.code && (
                          <Grid
                            xs={12}
                            item
                          >
                            <FormControlNumericInput
                              name={`productModels.${index}.price`}
                              label="Giá niêm yết"
                              required={isRetailProductGroup}
                              disabled={option?.status === "view"}
                              prefix="đ"
                              thousandSeparator
                            />
                            {!!money && (
                              <Typography
                                variant="caption"
                                fontStyle="italic"
                              >
                                ({convertCurrency(money)} đồng)
                              </Typography>
                            )}
                          </Grid>
                        )}
                      </Grid>

                      {/* Shipping Info Fields */}
                      {!prodGroupDetail?.data?.code && (
                        <>
                          <Grid
                            xs={3}
                            item
                          >
                            <FormControlInput
                              name={`productModels.${index}.shippingInfo.weight`}
                              label="Cân nặng sau đóng gói"
                              disabled={option?.status === "view"}
                              type="number"
                              slotProps={{
                                input: {
                                  endAdornment: (
                                    <InputAdornment
                                      position="end"
                                      sx={{
                                        pl: "12px",
                                        borderLeft: "1px solid var(--grey)",
                                      }}
                                    >
                                      g
                                    </InputAdornment>
                                  ),
                                },
                              }}
                            />
                          </Grid>

                          <Grid
                            xs={3}
                            item
                          >
                            <FormControlInput
                              name={`productModels.${index}.shippingInfo.length`}
                              label="Dài"
                              disabled={option?.status === "view"}
                              type="number"
                              slotProps={{
                                input: {
                                  endAdornment: (
                                    <InputAdornment
                                      position="end"
                                      sx={{
                                        pl: "12px",
                                        borderLeft: "1px solid var(--grey)",
                                      }}
                                    >
                                      cm
                                    </InputAdornment>
                                  ),
                                },
                              }}
                            />
                          </Grid>

                          <Grid
                            xs={3}
                            item
                          >
                            <FormControlInput
                              name={`productModels.${index}.shippingInfo.width`}
                              label="Rộng"
                              disabled={option?.status === "view"}
                              type="number"
                              slotProps={{
                                input: {
                                  endAdornment: (
                                    <InputAdornment
                                      position="end"
                                      sx={{
                                        pl: "12px",
                                        borderLeft: "1px solid var(--grey)",
                                      }}
                                    >
                                      cm
                                    </InputAdornment>
                                  ),
                                },
                              }}
                            />
                          </Grid>

                          <Grid
                            xs={3}
                            item
                          >
                            <FormControlInput
                              name={`productModels.${index}.shippingInfo.height`}
                              label="Cao"
                              type="number"
                              disabled={option?.status === "view"}
                              slotProps={{
                                input: {
                                  endAdornment: (
                                    <InputAdornment
                                      position="end"
                                      sx={{
                                        pl: "12px",
                                        borderLeft: "1px solid var(--grey)",
                                      }}
                                    >
                                      cm
                                    </InputAdornment>
                                  ),
                                },
                              }}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid
                        xs={12}
                        item
                      >
                        <Divider sx={{ borderColor: "var(--grey) !important" }} />
                      </Grid>
                    </Grid>
                  );
                })}

                <Grid
                  xs={12}
                  item
                  align="right"
                >
                  <Button
                    disabled={option?.status === "view"}
                    variant="outlined"
                    onClick={handleAddMoreClassification}
                    startIcon={<Add />}
                  >
                    Thêm phân loại
                  </Button>
                </Grid>
                <Grid
                  xs={12}
                  item
                  align="center"
                >
                  {option.status === ACTION_TYPE.Create && (
                    <Button
                      sx={{ mt: "24px" }}
                      disabled={option?.status === "view"}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      Lưu sản phẩm
                    </Button>
                  )}

                  {option.status === ACTION_TYPE.Update && (
                    <Button
                      fullWidth
                      onClick={handleUpdateProdInfo}
                      variant="contained"
                    >
                      Lưu sản phẩm
                    </Button>
                  )}
                </Grid>
              </Grid>
            ) : (
              <LoadingComp />
            )}
          </>
        ) : (
          <ProductDetailView item={prodDetail?.data} />
        )}
      </form>
    </FormProvider>
  );
}
